<script lang="ts" setup>
import Modal from './Modal.vue'
import Loading from './Loading.vue'
import { PropType, ref, computed } from 'vue'
import { Illust, IllustDetail, Comment, User, UserLite, Stamp } from '../../../libs/models'
import UserName from './UserName.vue'
import Stamps from './Stamps.vue'
import Date from './Date.vue'
import illustUrl from '../libs/illustUrl'
import IllustDeleteButton from './IllustDeleteButton.vue'
import { useApi } from '../libs/api'
import { useCurrentUserStore } from '../libs/currentUserStore'
import { useProfileModal, useTagModal } from '../libs/util'
import IllustEditModal from './IllustEditModal.vue'
import UserTextContent from './UserTextContent.vue'
import TestModal from './TestModal.vue'
import AlbumPickerModal from './AlbumPickerModal.vue'
import { getApiErrorMessage } from '../libs/apiErrors'
import { l } from '../libs/localization'

const emit = defineEmits(['close', 'deleted', 'updated'])
const props = defineProps({
  illust: { type: Object as PropType<Illust>, required: true }
})
const api = useApi()
const currentUserStore = useCurrentUserStore()
const showStamps = ref(false)
const illustDetail = ref<IllustDetail | null | undefined>(undefined)
const isLoadingDetail = computed(() => illustDetail.value === undefined)
const stampUsers = ref<Stamp[] | undefined>(undefined)
const isLoadingStampUsers = computed(() => stampUsers.value === undefined)
const displayIllust = computed(() => illustDetail.value || props.illust)
const comments = computed(() => illustDetail.value?.comments || [])
const stampGroup = computed(() => {
  return (stampUsers.value || []).reduce((info, stamp) => {
    const str = String.fromCodePoint(stamp.code)
    const row = info.find(v => v[0] === str)
    if (row) {
      row[1].push(stamp)
    } else {
      info.push([str, [stamp]])
    }
    return info
  }, [] as [string, Stamp[]][]).sort((a, b) => b[1].length - a[1].length)
})
const commentText = ref('')
const isSubmitting = ref(false)
const showEditModal = ref(false)
const showAlbumPickerModal = ref(false)
const { open: openProfileModal } = useProfileModal()
const { open: openTagModal } = useTagModal()

api.get(`/illusts/${props.illust.id}`).then(({ data }) => {
  illustDetail.value = data
}).catch(() => {
  illustDetail.value = null
})

const test = ref(false)
const submitComment = () => {
  if (!illustDetail.value) return
  if (!commentText.value.trim() || isSubmitting.value) return
  const currentUserId = currentUserStore.value?.id
  const latestComments = illustDetail.value.comments.slice(-10)
  if (
    currentUserId === illustDetail.value.user_id &&
    latestComments.length === 10 &&
    latestComments.every(comment => comment.user.id === currentUserId)
  ) {
    alert(l('これ以上のコメントを連続して投稿することはできません'))
    return
  }
  if (!localStorage.getItem('completedTest') && !localStorage.getItem('skipedTest')) {
    test.value = true
    return
  }
  isSubmitting.value = true
  api.post('/comments', {
    illustId: props.illust.id,
    text: commentText.value.trim()
  }).then(({ data }) => {
    if (!illustDetail.value) return
    illustDetail.value.comments.push(data)
    illustDetail.value.comment_count = (illustDetail.value.comment_count || 0) + 1
    props.illust.comment_count = illustDetail.value.comment_count
    commentText.value = ''
  }).catch(error => {
    console.error('Failed to submit comment:', error)
    if (error.response?.status === 422) {
      alert(l('コメントの内容が不適切な可能性があります。表現を修正のうえ再度送信してください。繰り返された場合は利用停止となりますのでご注意ください。'))
      commentText.value = ''
    } else alert(getApiErrorMessage(error, 'コメントの投稿に失敗しました'))
  }).finally(() => {
    isSubmitting.value = false
  })
}

const deleteComment = (comment: Comment) => {
  if (!illustDetail.value) return
  if (!confirm(l('このコメントを削除しますか？（元に戻せません）'))) return
  const index = illustDetail.value.comments.findIndex(c => c.id === comment.id)
  api.post(`/comments/${comment.id}/delete`).then(() => {
    if (!illustDetail.value) return
    if (index !== -1) {
      illustDetail.value.comments.splice(index, 1)
      illustDetail.value.comment_count = Math.max((illustDetail.value.comment_count || 1) - 1, 0)
      props.illust.comment_count = illustDetail.value.comment_count
    }
  }).catch(error => {
    console.error('Failed to delete comment:', error)
    alert(l('コメントの削除に失敗しました'))
  })
}

const canDeleteComment = (comment: Comment) => {
  if (!currentUserStore.value) return false
  return comment.user.id === currentUserStore.value.id || 
          displayIllust.value.user_id === currentUserStore.value.id
}

const canEditIllust = computed(() => {
  if (!currentUserStore.value) return false
  return displayIllust.value.user_id === currentUserStore.value.id
})

const openStampUsersModal = () => {
  showStamps.value = true
  stampUsers.value = undefined
  api.get(`/illusts/${props.illust.id}/stamps`).then(({ data }) => {
    stampUsers.value = data
  }).catch(() => {
    stampUsers.value = []
  })
}

const isDeletingStamp = ref(false)
const deleteStampGroup = (code: number) => {
  if (!canEditIllust.value || isDeletingStamp.value) return
  if (!confirm(l('このイラストにつけられた「{stamp}」のスタンプをすべて削除しますか？（元に戻せません）', { stamp: String.fromCodePoint(code) }))) return
  isDeletingStamp.value = true
  api.post(`/illusts/${props.illust.id}/stamps/delete`, { code }).then(() => {
    stampUsers.value = stampUsers.value?.filter(stamp => stamp.code !== code)
    props.illust.stamps = props.illust.stamps.filter(stamp => stamp.code !== code)
    if (illustDetail.value) {
      illustDetail.value.stamps = illustDetail.value.stamps.filter(stamp => stamp.code !== code)
      illustDetail.value.ownStamps = illustDetail.value.ownStamps.filter(stamp => stamp.code !== code)
    }
  }).catch(() => {
    alert(l('スタンプの削除に失敗しました'))
  }).finally(() => {
    isDeletingStamp.value = false
  })
}

const openUser = (user: User | UserLite | number) => {
  openProfileModal(user)
  emit('close')
}
const openTag = (tag: string) => {
  openTagModal(tag)
  emit('close')
}
</script>

<template>
  <TestModal v-if="test" @close="test = false" />
  <IllustEditModal v-else-if="showEditModal && illustDetail" :illust="illustDetail" @updated="$emit('updated')" @close="showEditModal = false" />
  <AlbumPickerModal v-else-if="showAlbumPickerModal && currentUserStore.value" :illust-id="displayIllust.id" @close="showAlbumPickerModal = false" />
  <Modal v-else-if="showStamps" @close="showStamps = false">
    <div class="ModalContent">
      <div class="ModalTitle">{{ l('『{title}』にスタンプをつけた人', { title: displayIllust.title }) }}</div>
      <Loading v-if="isLoadingStampUsers" />
      <ul v-else>
        <li v-for="row in stampGroup" :key="row[0]" class="StampListItem">
          <div class="StampListItem-emoji">{{ row[0] }}</div>
          <div class="StampListItem-user">
            <template v-for="(v, i) in row[1]" :key="v.user_id">
              {{ i ? ', ' : '' }}<span v-if="v.user_id === currentUserStore.value?.id">{{ v.user_name }}</span>
              <a v-else href="#" @click.prevent="openUser(v.user_id)">{{ v.user_name }}</a>
            </template>
          </div>
          <button v-if="canEditIllust" class="StampListItem-delete" type="button" :disabled="isDeletingStamp" :aria-label="l('{stamp}のスタンプをすべて削除', { stamp: row[0] })" :title="l('この種類のスタンプをすべて削除')" @click="deleteStampGroup(row[1][0].code)">
            <span class="material-symbols-rounded" aria-hidden="true">delete</span>
          </button>
        </li>
      </ul>
      <button class="cancel" @click="showStamps = false">{{ l('閉じる') }}</button>
    </div>
  </Modal>
  <Modal v-else class="IllustDetailModal" @close="$emit('close')">
    <div class="ModalContent">
      <div class="ModalTitle">『{{ displayIllust.title }}』 <small>- <Date :datetime="displayIllust.created" :full="true" /></small></div>
      <div class="Illust">
        <a :href="illustUrl(displayIllust.user_id, displayIllust.id)" target="_blank">
          <img :src="illustUrl(displayIllust.user_id, displayIllust.id)">
        </a>
      </div>
      <div v-if="displayIllust.user" class="Author">
        by <UserName :user="displayIllust.user" :emit="true" @click="openUser(displayIllust.user)" />
      </div>
      <div class="Tags" v-if="displayIllust.tags.length">
        <a class="Tag" href="#" v-for="(tag, i) in displayIllust.tags" :key="i" @click.prevent="openTag(tag)">{{ tag }}</a>
      </div>
      <Stamps :illust="displayIllust" :readonly="!illustDetail" :own-stamps="illustDetail?.ownStamps || []" />
      <div class="StampUsersLink"><a href="#" @click.prevent="openStampUsersModal">{{ l('スタンプをつけた人') }}</a></div>
      <button v-if="currentUserStore.value" @click="showAlbumPickerModal = true"><i class="material-symbols-rounded">bookmark_add</i>{{ l('アルバムへ追加') }}</button>
      <button v-if="canEditIllust && illustDetail" @click="showEditModal = true"><span class="material-symbols-rounded" aria-hidden="true">edit</span>{{ l('編集') }}</button>
      <button class="cancel" @click="$emit('close')">{{ l('閉じる') }}</button>
      <IllustDeleteButton :illust="displayIllust" @deleted="$emit('deleted')" />
      <div class="Comments" v-if="illustDetail?.desc">
        <div class="Comments-header">{{ l('キャプション') }}</div>
        <UserTextContent class="Comment-text" :text="illustDetail.desc" is="div" @tagClick="$emit('close')" />
      </div>
      <div class="Comments">
        <div class="Comments-header">{{ l('コメント') }}<small>（{{ comments.length }}）</small></div>
        <Loading v-if="isLoadingDetail" />
        <div class="Comments-list" v-else-if="comments.length > 0">
          <div v-for="comment in comments" :key="comment.id" class="Comment">
            <div class="Comment-header">
              <UserName :user="comment.user" :emit="true" @click="openUser(comment.user)" />
              <Date :datetime="comment.created" />
              <span v-if="canDeleteComment(comment)">[<a href="#" @click="deleteComment(comment)">{{ l('削除') }}</a>]</span>
            </div>
            <UserTextContent class="Comment-text" :text="comment.text" is="div" @tagClick="$emit('close')" />
          </div>
        </div>
        <div class="Comments-empty" v-else>
          {{ l('まだコメントがありません') }}
        </div>
        <form class="Comments-form" @submit.prevent="submitComment" v-if="currentUserStore.value && !isLoadingDetail">
          <textarea v-model="commentText" required maxlength="255" :disabled="isSubmitting" />
          <p class="Warning">
            {{ l('作品を侮辱するコメントをAIが検知した場合に利用停止となります。') }}<br>
            {{ l('また、ジョークや悪ノリ、自虐などが批判的コメントとして誤検知される場合がありますのでご注意ください。') }}
          </p>
          <Loading v-if="isSubmitting" :size="24" />
          <button v-else type="submit" :disabled="!commentText.trim()">{{ l('コメントを送信') }}</button>
        </form>
      </div>
      <button class="cancel" @click="$emit('close')">{{ l('閉じる') }}</button>
    </div>
  </Modal>
</template>

<style lang="scss" scoped>
.ModalContent {
  line-height: 1.5;
}
@include spLandscape {
  .IllustDetailModal :deep(.ModalContainer) {
    max-width: 400px;
  }
}
.Illust {
  margin: 6px auto;
  overflow: hidden;
  img {
    display: block;
    height: 100%;
    margin: auto;
    border-radius: 10px;
  }
}
.Author {
  margin-top: 11px;
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  .userNameWrapper {
    margin-left: 7px;
  }
}
button {
  margin-top: 16px;
}
.Stamps {
  margin: 12px auto 8px;
}
.StampUsersLink {
  margin-top: 12px;
  text-align: center;
  font-size: 12px;
}
.StampListItem {
  display: flex;
  align-items: center;
  padding: 5px 0;
  border-bottom: 1px dashed var(--border-color);
  &-emoji {
    margin-right: 1em;
    font-size: 16px;
  }
  &-user {
    flex: 1;
    min-width: 0;
    overflow-wrap: anywhere;
    line-height: 1.4;
  }
  &-delete {
    flex-shrink: 0;
    width: auto;
    margin: 0 0 0 8px;
    padding: 4px;
    background: transparent;
    color: var(--tx-color-light);
    border: none;
    box-shadow: none;
    text-shadow: none;
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
    .material-symbols-rounded {
      font-size: 18px;
      margin-right: 0;
    }
  }
}
.Comments {
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--border-color);
  &-header {
    font-weight: bold;
    margin-bottom: 10px;
  }
  &-list {
    margin-bottom: 15px;
  }
  &-empty {
    color: var(--text-color-light);
    margin-bottom: 15px;
    text-align: center;
  }
  &-form {
    textarea {
      margin-bottom: 15px;
      flex: 1;
      padding: 8px 12px;
      border: 1px solid var(--border-color);
      border-radius: 5px;
      background: var(--input-bg);
      color: var(--text-color);
    }
    button {
      padding: 8px 16px;
      margin-top: 0;
      white-space: nowrap;
    }
  }
}

.Comment {
  padding: 10px 0;
  border-bottom: 1px dashed var(--border-color);
  &:last-child {
    border-bottom: none;
  }
  &-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 5px;
    font-size: 0.9em;
  }
  &-text {
    margin-left: 5px;
    word-break: break-word;
    white-space: pre-wrap;
  }
}

.Warning {
  font-size: 11px;
  color: var(--red);
  text-align: center;
  margin-bottom: 10px;
}
</style>
